import { useMemo, useState } from "react";
import {
  NAU_LOCALIDADES, GENERO_OPCIONES, OCUPACION_OPCIONES, NET_TEMAS,
  COMO_SE_ENTERO_OPCIONES, esMenor,
} from "./nauConfig";
import { enviarPersona, enviarGrupoBackend } from "./nauSubmit";

// ============================================================================
//  Formulario de inscripción — NAUFest Uruguay 2026
//  Réplica del formulario original (index_3.html): mismos textos, logo, modal
//  de agradecimiento y footer. Sin el campo Celular (queda fuera).
//
//  Dos modos:
//    · Individual        → una persona, 1 envío.
//    · Grupal (Docente)  → docente + N estudiantes; se envían UNO A UNO al
//                          mismo form de FormAssembly (rol automático:
//                          docente = Profesor, estudiantes = Alumno).
//                          Si uno falla, sigue con los demás y muestra resumen.
//
//  Networking: solo para mayores (fecha de corte en nauConfig). A los menores
//  se les oculta y se envía "No".
// ============================================================================

const LOGO_URL = "https://naufest.com/wp-content/uploads/2026/06/LOGO-NAUFEST-mas-grande.png";

// Formato de email válido (algo@algo.dominio).
const emailValido = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((v || "").trim());

const personaVacia = (rol) => ({
  rol,
  nombre: "", apellido: "", cedula: "", email: "", fechaNac: "",
  genero: "", departamento: "", localidad: "",
  ocupacion: "", institucionEducativa: "",
  networking: "No", net1: "", net2: "", net3: "",
  autorizacion: "Si",
});

export default function NauFormulario() {
  const [tipo, setTipo] = useState("individual");

  const [individual, setIndividual] = useState(personaVacia("alumno"));
  const [comoSeEntero, setComoSeEntero] = useState("");
  const [recibirInfo, setRecibirInfo] = useState("");

  const [docente, setDocente] = useState(personaVacia("profesor"));
  const [centroEducativo, setCentroEducativo] = useState("");
  const [grupoDepartamento, setGrupoDepartamento] = useState("");
  const [grupoLocalidad, setGrupoLocalidad] = useState("");
  const [estudiantes, setEstudiantes] = useState([personaVacia("alumno")]);

  const [enviando, setEnviando] = useState(false);
  const [mostrarModal, setMostrarModal] = useState(false);
  const [errorGeneral, setErrorGeneral] = useState("");

  const departamentos = NAU_LOCALIDADES.departamentos;
  const localidadesDe = (depLabel) =>
    departamentos.find((d) => d.label === depLabel)?.localidades || [];

  const setIndCampo = (campo, val) =>
    setIndividual((p) => {
      const next = { ...p, [campo]: val };
      if (campo === "departamento") next.localidad = "";
      if (campo === "fechaNac" && esMenor(val)) { next.networking = "No"; next.net1 = next.net2 = next.net3 = ""; }
      return next;
    });

  const setDocCampo = (campo, val) =>
    setDocente((p) => {
      const next = { ...p, [campo]: val };
      if (campo === "departamento") next.localidad = "";
      if (campo === "fechaNac" && esMenor(val)) { next.networking = "No"; next.net1 = next.net2 = next.net3 = ""; }
      return next;
    });

  const setEstCampo = (i, campo, val) =>
    setEstudiantes((arr) => arr.map((e, idx) => {
      if (idx !== i) return e;
      const next = { ...e, [campo]: val };
      if (campo === "departamento") next.localidad = "";
      if (campo === "fechaNac" && esMenor(val)) { next.networking = "No"; next.net1 = next.net2 = next.net3 = ""; }
      return next;
    }));

  const agregarEstudiante = () => setEstudiantes((arr) => [...arr, personaVacia("alumno")]);
  const eliminarEstudiante = (i) => setEstudiantes((arr) => arr.filter((_, idx) => idx !== i));

  const personaCompleta = (p) =>
    p.nombre && p.apellido && p.cedula && emailValido(p.email) && p.fechaNac && p.genero &&
    p.departamento && p.localidad;

  // En grupal, depto/localidad son del grupo (no por persona).
  const personaCompletaGrupo = (p) =>
    p.nombre && p.apellido && p.cedula && emailValido(p.email) && p.fechaNac && p.genero;

  const puedeEnviar = useMemo(() => {
    if (tipo === "individual") return personaCompleta(individual);
    return personaCompletaGrupo(docente) && centroEducativo &&
      grupoDepartamento && grupoLocalidad &&
      estudiantes.length > 0 && estudiantes.every(personaCompletaGrupo);
  }, [tipo, individual, docente, centroEducativo, grupoDepartamento, grupoLocalidad, estudiantes]);

  // Deja el formulario en blanco (se llama tras un envío exitoso).
  const limpiarFormulario = () => {
    setIndividual(personaVacia("alumno"));
    setComoSeEntero("");
    setRecibirInfo("");
    setDocente(personaVacia("profesor"));
    setCentroEducativo("");
    setGrupoDepartamento("");
    setGrupoLocalidad("");
    setEstudiantes([personaVacia("alumno")]);
  };

  const enviar = async () => {
    setEnviando(true);
    setErrorGeneral("");
    try {
      if (tipo === "individual") {
        const persona = { ...individual, comoSeEntero, recibirInfo };
        const r = await enviarPersona(persona);
        if (r.ok) { setMostrarModal(true); limpiarFormulario(); }
        else setErrorGeneral(r.message || "No se pudo enviar la inscripción.");
      } else {
        const ubic = { departamento: grupoDepartamento, localidad: grupoLocalidad };
        const lista = [
          { ...docente, ...ubic, rol: "profesor", centroEducativo },
          ...estudiantes.map((e) => ({ ...e, ...ubic, rol: "alumno", centroEducativo })),
        ];
        // Una sola llamada: el backend procesa por detrás.
        const r = await enviarGrupoBackend(lista, centroEducativo);
        if (r.ok) { setMostrarModal(true); limpiarFormulario(); }
        else setErrorGeneral(r.message || "No se pudo enviar la inscripción del grupo.");
      }
    } catch (err) {
      setErrorGeneral(err.message || "Error inesperado al enviar.");
    } finally {
      setEnviando(false);
    }
  };

  return (
    <div style={S.page}>
      <div style={S.accentBar} />

      <div style={S.logoWrap}>
        <img src={LOGO_URL} alt="NAUFest Logo" style={S.logo} />
      </div>

      <div style={S.card}>
        <h1 style={S.title}>Formulario de Inscripción – NAUFest Uruguay 2026</h1>

        <div style={S.intro}>
          <p style={S.introStrong}>¡Gracias por tu interés en participar en NAUFest Uruguay 2026!</p>
          <p>
            NAUFest es una experiencia educativa impulsada por Junior Achievement que conecta a
            jóvenes con profesionales, empresas, referentes e iniciativas vinculadas al futuro del
            trabajo, sustentabilidad, el emprendimiento, la tecnología, la inteligencia artificial
            y el desarrollo personal.
          </p>
          <p>
            Este formulario nos permitirá gestionar tu participación, asignarte espacios de
            mentoreo según tus intereses y mejorar la experiencia del evento.
          </p>
          <p>
            Una vez que completes el formulario, te enviaremos tu entrada con un código QR al correo
            electrónico registrado. Si no la encontrás en tu bandeja de entrada, por favor revisá tu
            carpeta de Spam. En caso de no verla allí o si tenés algún problema con la inscripción,
            podés contactarte con nosotros a través de info@desem.org.uy o por nuestras redes sociales.
          </p>
          <p style={S.introStrong}>
            ¡Nos alegra que quieras ser parte de esta red de conexiones que construye oportunidades!
          </p>
        </div>

        <Campo label="Tipo de Registro" requerido>
          <select style={S.input} value={tipo}
            onChange={(e) => { setTipo(e.target.value); setErrorGeneral(""); }}>
            <option value="individual">Inscripción Individual</option>
            <option value="grupal">Inscripción Grupal (Docente)</option>
          </select>
        </Campo>

        {tipo === "individual" ? (
          <PersonaCampos
            persona={individual}
            set={setIndCampo}
            departamentos={departamentos}
            localidades={localidadesDe(individual.departamento)}
            conInstitucion
            extra={{ comoSeEntero, setComoSeEntero, recibirInfo, setRecibirInfo }}
          />
        ) : (
          <>
            <SeccionTitulo>Datos del Docente</SeccionTitulo>
            <div style={S.grid2}>
              <Campo label="Nombre del Docente" requerido>
                <input style={S.input} value={docente.nombre}
                  onChange={(e) => setDocCampo("nombre", e.target.value)} placeholder="Nombre" />
              </Campo>
              <Campo label="Apellido del Docente" requerido>
                <input style={S.input} value={docente.apellido}
                  onChange={(e) => setDocCampo("apellido", e.target.value)} placeholder="Apellido" />
              </Campo>
            </div>
            <div style={S.grid2}>
              <Campo label="Cédula de identidad (CI)" requerido>
                <input style={S.input} value={docente.cedula}
                  onChange={(e) => setDocCampo("cedula", e.target.value)} placeholder="Cédula" />
              </Campo>
              <Campo label="Correo electrónico del Docente" requerido>
                <input style={{ ...S.input, ...(docente.email && !emailValido(docente.email) ? S.inputError : {}) }}
                  type="email" value={docente.email}
                  onChange={(e) => setDocCampo("email", e.target.value)} placeholder="docente@institucion.edu" />
                {docente.email && !emailValido(docente.email) && (
                  <span style={S.errorMsg}>Ingresá un correo válido (ej: nombre@dominio.com)</span>
                )}
              </Campo>
            </div>
            <div style={S.grid2}>
              <Campo label="Fecha de nacimiento" requerido>
                <input style={S.input} type="date" value={docente.fechaNac}
                  onChange={(e) => setDocCampo("fechaNac", e.target.value)} />
              </Campo>
              <Campo label="Género del Docente" requerido>
                <select style={S.input} value={docente.genero}
                  onChange={(e) => setDocCampo("genero", e.target.value)}>
                  <option value="">Seleccioná una opción</option>
                  {GENERO_OPCIONES.map((g) => <option key={g} value={g}>{g}</option>)}
                </select>
              </Campo>
            </div>
            <Campo label="Centro Educativo" requerido>
              <input style={S.input} value={centroEducativo}
                placeholder="Nombre del centro educativo"
                onChange={(e) => setCentroEducativo(e.target.value)} />
            </Campo>

            <SeccionTitulo>Ubicación del grupo</SeccionTitulo>
            <p style={S.hintP}>
              Se aplica al docente y a todos los estudiantes del grupo.
            </p>
            <div style={S.grid2}>
              <Campo label="Departamento" requerido>
                <select style={S.input} value={grupoDepartamento}
                  onChange={(e) => { setGrupoDepartamento(e.target.value); setGrupoLocalidad(""); }}>
                  <option value="">Seleccioná el departamento</option>
                  {departamentos.map((d) => <option key={d.val} value={d.label}>{d.label}</option>)}
                </select>
              </Campo>
              <Campo label="Localidad" requerido>
                <select style={S.input} value={grupoLocalidad} disabled={!grupoDepartamento}
                  onChange={(e) => setGrupoLocalidad(e.target.value)}>
                  <option value="">
                    {grupoDepartamento ? "Seleccioná la localidad" : "Elegí primero el departamento"}
                  </option>
                  {localidadesDe(grupoDepartamento).map((l) => <option key={l.val} value={l.label}>{l.label}</option>)}
                </select>
              </Campo>
            </div>

            <SeccionTitulo>Estudiantes</SeccionTitulo>
            <p style={S.hintP}>
              Complete la información correspondiente para cada uno de los estudiantes del grupo.
            </p>
            {estudiantes.map((est, i) => (
              <div key={i} style={S.studentCard}>
                <div style={S.studentHeader}>
                  <span style={S.studentTitle}>Estudiante #{i + 1}</span>
                  {estudiantes.length > 1 && (
                    <button type="button" style={S.btnRemove}
                      onClick={() => eliminarEstudiante(i)}>Eliminar</button>
                  )}
                </div>
                <PersonaCampos
                  persona={est}
                  set={(campo, val) => setEstCampo(i, campo, val)}
                  departamentos={departamentos}
                  localidades={localidadesDe(est.departamento)}
                  ocultarUbicacion
                />
              </div>
            ))}
            <button type="button" style={S.btnAdd} onClick={agregarEstudiante}>
              + Agregar otro estudiante
            </button>
          </>
        )}

        {errorGeneral && <div style={S.error}>{errorGeneral}</div>}

        <div style={S.legal}>
          <p>
           Los datos personales solicitados en este formulario, incluido el número de cédula de identidad, serán tratados por DESEM Junior Achievement Uruguay exclusivamente para identificar de manera unívoca a las personas participantes, gestionar e implementar los programas educativos, evitar registros duplicados, monitorear y evaluar los resultados, medir el impacto institucional, elaborar estadísticas y cumplir con las obligaciones de seguimiento, auditoría y rendición de cuentas ante organismos competentes, financiadores y donantes, cuando corresponda.
 
          </p>
          <p>Durante el evento se realizará cobertura de fotografía y video, realizada por profesionales en la materia. A través este texto se autoriza a DESEM Junior Achievement a utilizar dichas imágenes para la difusión del evento en redes sociales, medios de comunicación, entre otros. 
 </p>
          <p>
            DESEM no utilizará esta información para fines comerciales ni la compartirá con empresas
            patrocinadoras o terceros para finalidades distintas de las aquí informadas. El
            tratamiento de los datos se realizará con estricta confidencialidad y aplicando medidas
            de seguridad acordes a la normativa vigente, de conformidad con la Ley Nº 18.331 de
            Protección de Datos Personales.
          </p>
          <p>
            Los informes de resultados elaborados por DESEM presentan información consolidada y
            estadística, sin identificar individualmente a las personas participantes.
          </p>
        </div>

        <button type="button" style={{ ...S.submit, ...((!puedeEnviar || enviando) ? S.submitOff : {}) }}
          disabled={!puedeEnviar || enviando} onClick={enviar}>
          {enviando ? "Enviando…" : "Enviar Registro"}
        </button>
      </div>

      <footer style={S.footer}>
        © 2026 DESEM Junior Achievement Uruguay · Todos los derechos reservados
      </footer>

      {mostrarModal && (
        <div style={S.modalOverlay}>
          <div style={S.modal}>
            <h3 style={S.modalTitle}>🎉 ¡Gracias por registrarte en NAUFest!</h3>
            {tipo === "grupal" ? (
              <>
                <p style={S.modalText}>
                  Recibimos la inscripción del grupo. Estamos procesando cada registro y
                  enviando las entradas con código QR a los correos indicados.
                </p>
                <p style={S.modalText}>
                  Si algún correo no llega, revisá la carpeta de Spam o comunicate con nosotros a
                  través de info@desem.org.uy o por nuestras redes sociales.
                </p>
              </>
            ) : (
              <>
                <p style={S.modalText}>
                  Te hemos enviado un correo electrónico con tu entrada que contiene un código QR.
                </p>
                <p style={S.modalText}>
                  Si no lo encontrás en tu bandeja de entrada, por favor revisá tu carpeta de Spam o
                  comunicate con nosotros a través de info@desem.org.uy o por nuestras redes sociales.
                </p>
              </>
            )}
            <button type="button" style={S.modalBtn}
              onClick={() => setMostrarModal(false)}>Entendido</button>
          </div>
        </div>
      )}
    </div>
  );
}

function PersonaCampos({ persona, set, departamentos, localidades, conInstitucion, ocultarUbicacion, extra }) {
  const menor = esMenor(persona.fechaNac);
  return (
    <>
      <div style={S.grid2}>
        <Campo label="Nombre" requerido>
          <input style={S.input} value={persona.nombre}
            onChange={(e) => set("nombre", e.target.value)} placeholder="Nombre" />
        </Campo>
        <Campo label="Apellido" requerido>
          <input style={S.input} value={persona.apellido}
            onChange={(e) => set("apellido", e.target.value)} placeholder="Apellido" />
        </Campo>
      </div>

      <div style={S.grid2}>
        <Campo label="Cédula de identidad" requerido>
          <input style={S.input} value={persona.cedula}
            onChange={(e) => set("cedula", e.target.value)} placeholder="Sin puntos ni guiones" />
        </Campo>
        <Campo label="Correo electrónico" requerido>
          <input style={{ ...S.input, ...(persona.email && !emailValido(persona.email) ? S.inputError : {}) }}
            type="email" value={persona.email}
            onChange={(e) => set("email", e.target.value)} placeholder="tu@email.com" />
          {persona.email && !emailValido(persona.email) && (
            <span style={S.errorMsg}>Ingresá un correo válido (ej: nombre@dominio.com)</span>
          )}
        </Campo>
      </div>

      <div style={S.grid2}>
        <Campo label="Fecha de nacimiento" requerido>
          <input style={S.input} type="date" value={persona.fechaNac}
            onChange={(e) => set("fechaNac", e.target.value)} />
        </Campo>
        <Campo label="Género" requerido>
          <select style={S.input} value={persona.genero}
            onChange={(e) => set("genero", e.target.value)}>
            <option value="">Seleccioná una opción</option>
            {GENERO_OPCIONES.map((g) => <option key={g} value={g}>{g}</option>)}
          </select>
        </Campo>
      </div>

      {!ocultarUbicacion && (
        <div style={S.grid2}>
          <Campo label="Departamento de residencia" requerido>
            <select style={S.input} value={persona.departamento}
              onChange={(e) => set("departamento", e.target.value)}>
              <option value="">Seleccioná tu departamento</option>
              {departamentos.map((d) => <option key={d.val} value={d.label}>{d.label}</option>)}
            </select>
          </Campo>
          <Campo label="Localidad" requerido>
            <select style={S.input} value={persona.localidad} disabled={!persona.departamento}
              onChange={(e) => set("localidad", e.target.value)}>
              <option value="">
                {persona.departamento ? "Seleccioná tu localidad" : "Elegí primero el departamento"}
              </option>
              {localidades.map((l) => <option key={l.val} value={l.label}>{l.label}</option>)}
            </select>
          </Campo>
        </div>
      )}

      {conInstitucion && (
        <Campo label="Institución educativa" requerido>
          <input style={S.input} value={persona.institucionEducativa}
            onChange={(e) => set("institucionEducativa", e.target.value)}
            placeholder="Nombre de la institución" />
        </Campo>
      )}

      <Campo label="¿Qué estás haciendo actualmente?" requerido>
        <select style={S.input} value={persona.ocupacion}
          onChange={(e) => set("ocupacion", e.target.value)}>
          <option value="">Seleccioná una opción</option>
          {OCUPACION_OPCIONES.map((o) => <option key={o} value={o}>{o}</option>)}
        </select>
      </Campo>

      {!menor && (
        <div style={S.netWrap}>
          <p style={S.netIntro}>
            Esta instancia busca generar un espacio de intercambio cercano entre jóvenes y
            profesionales. Destinada a jóvenes de entre 18 y 25 años.
          </p>
          <Campo label="¿Te gustaría participar de las mesas de mentoreo?" requerido>
            <select style={S.input} value={persona.networking}
              onChange={(e) => set("networking", e.target.value)}>
              <option value="No">No</option>
              <option value="Si">Sí</option>
            </select>
          </Campo>
          {persona.networking === "Si" && (
            <>
              <Campo label="Seleccioná tu tema de mentoreo preferido">
                <select style={S.input} value={persona.net1}
                  onChange={(e) => set("net1", e.target.value)}>
                  <option value="">Seleccioná una opción</option>
                  {NET_TEMAS.map((t) => <option key={t.val} value={t.val}>{t.label}</option>)}
                </select>
              </Campo>
              <Campo label="Seleccioná una segunda preferencia">
                <select style={S.input} value={persona.net2}
                  onChange={(e) => set("net2", e.target.value)}>
                  <option value="">Seleccioná una opción</option>
                  {NET_TEMAS.map((t) => <option key={t.val} value={t.val}>{t.label}</option>)}
                </select>
              </Campo>
              <Campo label="Seleccioná una tercera preferencia">
                <select style={S.input} value={persona.net3}
                  onChange={(e) => set("net3", e.target.value)}>
                  <option value="">Seleccioná una opción</option>
                  {NET_TEMAS.map((t) => <option key={t.val} value={t.val}>{t.label}</option>)}
                </select>
              </Campo>
            </>
          )}
        </div>
      )}

      {extra && (
        <>
          <Campo label="¿Cómo te enteraste de NAUFest?" requerido>
            <select style={S.input} value={extra.comoSeEntero}
              onChange={(e) => extra.setComoSeEntero(e.target.value)}>
              <option value="">Seleccioná una opción</option>
              {COMO_SE_ENTERO_OPCIONES.map((o) => <option key={o} value={o}>{o}</option>)}
            </select>
          </Campo>
          <Campo label="¿Querés recibir información sobre futuras actividades de DESEM Junior Achievement?">
            <select style={S.input} value={extra.recibirInfo}
              onChange={(e) => extra.setRecibirInfo(e.target.value)}>
              <option value="">Seleccioná una opción</option>
              <option value="Si">Sí</option>
              <option value="No">No</option>
            </select>
          </Campo>
        </>
      )}
    </>
  );
}

function Campo({ label, requerido, children }) {
  return (
    <div style={S.group}>
      <label style={S.label}>{label}{requerido && <span style={S.req}> *</span>}</label>
      {children}
    </div>
  );
}

function SeccionTitulo({ children }) {
  return <h2 style={S.seccionTitulo}>{children}</h2>;
}

const CYAN = "#00BCD4";
const S = {
  page: { fontFamily: "'Montserrat',-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif",
    background: "#EFEFEF", minHeight: "100vh", color: "#1E293B",
    display: "flex", flexDirection: "column" },
  accentBar: { width: "100%", height: 12, background: CYAN },
  logoWrap: { maxWidth: 1100, margin: "2rem auto 0", padding: "0 1.5rem", textAlign: "center" },
  logo: { maxWidth: 220, height: "auto" },
  card: { maxWidth: 720, width: "100%", margin: "1.5rem auto", background: "#fff",
    borderRadius: 16, padding: "2rem", boxShadow: "0 10px 30px rgba(0,0,0,.06)", boxSizing: "border-box" },
  title: { fontSize: 24, fontWeight: 800, letterSpacing: "-0.02em", lineHeight: 1.25 },
  intro: { margin: "1rem 0 1.5rem", color: "#475569", fontSize: 14, lineHeight: 1.6,
    display: "flex", flexDirection: "column", gap: "0.75rem" },
  introStrong: { fontWeight: 700, color: "#1E293B" },
  seccionTitulo: { fontSize: 16, fontWeight: 700, margin: "1.75rem 0 0.75rem",
    paddingBottom: 8, borderBottom: `2px solid ${CYAN}` },
  hintP: { fontSize: 13, color: "#64748B", marginBottom: "1rem" },
  group: { marginBottom: "1rem" },
  label: { display: "block", fontSize: 13, fontWeight: 600, marginBottom: 6, color: "#334155" },
  req: { color: CYAN },
  input: { width: "100%", padding: "11px 12px", fontSize: 14, borderRadius: 10,
    border: "1px solid #CBD5E1", outline: "none", fontFamily: "inherit",
    background: "#fff", color: "#1E293B", boxSizing: "border-box" },
  inputError: { border: "1px solid #DC2626", background: "#FEF2F2" },
  errorMsg: { display: "block", color: "#DC2626", fontSize: 12, marginTop: 4 },
  grid2: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 },
  studentCard: { border: "1px solid #E2E8F0", borderRadius: 12, padding: "1rem",
    marginBottom: "1rem", background: "#FAFCFD" },
  studentHeader: { display: "flex", justifyContent: "space-between", alignItems: "center",
    marginBottom: "0.75rem" },
  studentTitle: { fontWeight: 700, fontSize: 14, color: CYAN },
  btnRemove: { background: "none", border: "1px solid #FCA5A5", color: "#DC2626",
    borderRadius: 8, padding: "5px 12px", fontSize: 12, cursor: "pointer", fontFamily: "inherit" },
  btnAdd: { width: "100%", padding: "12px", borderRadius: 10, border: `2px dashed ${CYAN}`,
    background: "#F0FDFF", color: "#0E7490", fontWeight: 700, cursor: "pointer",
    fontSize: 14, fontFamily: "inherit", marginBottom: "1rem" },
  netWrap: { marginTop: "0.5rem" },
  netIntro: { fontSize: 13, color: "#64748B", lineHeight: 1.5, marginBottom: "0.75rem",
    background: "#F8FAFC", borderLeft: `3px solid ${CYAN}`, padding: "0.6rem 0.85rem", borderRadius: 6 },
  progresoBox: { margin: "1rem 0", fontSize: 14, fontWeight: 600, color: "#0E7490" },
  progresoTrack: { height: 6, background: "#E2E8F0", borderRadius: 4, marginTop: 8, overflow: "hidden" },
  progresoFill: { height: "100%", background: CYAN, transition: "width .3s ease" },
  resumen: { margin: "1rem 0", padding: "1rem", borderRadius: 10, background: "#F8FAFC",
    border: "1px solid #E2E8F0", fontSize: 14 },
  resumenLista: { margin: "0.5rem 0", paddingLeft: 18, color: "#DC2626", fontSize: 13 },
  btnRetry: { marginTop: 8, padding: "9px 16px", borderRadius: 8, border: "none",
    background: "#F59E0B", color: "#fff", fontWeight: 700, cursor: "pointer", fontFamily: "inherit" },
  error: { margin: "1rem 0", padding: "0.75rem 1rem", borderRadius: 10,
    background: "#FEF2F2", border: "1px solid #FECACA", color: "#DC2626", fontSize: 14 },
  legal: { margin: "1.5rem 0 0", fontSize: 11.5, color: "#94A3B8", lineHeight: 1.55,
    display: "flex", flexDirection: "column", gap: "0.6rem" },
  submit: { width: "100%", marginTop: "1.5rem", padding: "14px", borderRadius: 12, border: "none",
    background: CYAN, color: "#fff", fontSize: 16, fontWeight: 800, cursor: "pointer",
    fontFamily: "inherit", letterSpacing: "0.01em" },
  submitOff: { background: "#CBD5E1", cursor: "not-allowed" },
  footer: { textAlign: "center", padding: "1.5rem", fontSize: 12, color: "#94A3B8" },
  modalOverlay: { position: "fixed", inset: 0, background: "rgba(15,23,42,.55)",
    display: "flex", alignItems: "center", justifyContent: "center", padding: 20, zIndex: 50 },
  modal: { background: "#fff", borderRadius: 16, padding: "2rem", maxWidth: 440, width: "100%",
    textAlign: "center", boxShadow: "0 20px 50px rgba(0,0,0,.25)" },
  modalTitle: { fontSize: 20, fontWeight: 800, marginBottom: "0.75rem" },
  modalText: { fontSize: 14, color: "#475569", lineHeight: 1.55, marginBottom: "0.75rem" },
  modalBtn: { marginTop: "0.5rem", padding: "11px 28px", borderRadius: 10, border: "none",
    background: CYAN, color: "#fff", fontWeight: 700, fontSize: 15, cursor: "pointer", fontFamily: "inherit" },
};